import { useState } from 'react';
import type { EmployeeSkillsResponse, SourceTypeCode } from '../../api/types';
import { PROFICIENCY_COPY, formatYears } from './factDisplay';
import { evidenceTypeLabel, formatCalendarDate, formatTimestamp } from './attributeDisplay';
import { SkillEvidenceForm } from './SkillEvidenceForm';
import styles from './FactRecords.module.css';
import captureStyles from './CaptureSection.module.css';
import formStyles from './AssessmentForm.module.css';

/**
 * The Skills section (docs/UX_ARCHITECTURE.md §3.4).
 *
 * ---------------------------------------------------------------------
 * A SKILL IS NOT AN ATTRIBUTE
 *
 * This section sits directly beneath Attributes, which makes the domain
 * distinction (docs/DOMAIN_MODEL.md §2.7 vs §2.6) easy to lose by
 * accident. It is held apart deliberately, in three ways:
 *
 * 1. **Proficiency is a word, never a number.** There is no 1-20 value, no
 *    bar and no band colour here. A skill carries one recorded proficiency
 *    from one author; an attribute is a weighted composite of several
 *    source types with a confidence level attached. Rendering them alike
 *    would claim a parity that does not exist.
 * 2. **No confidence, variance or trajectory.** Those belong to the
 *    multi-source assessment model and simply do not apply to a skill
 *    record — showing an empty version of each would imply the concepts
 *    exist here and happen to be unpopulated.
 * 3. **Nothing is inferred between them.** Years recorded against a skill
 *    do not raise its proficiency, an Experience record does not imply a
 *    proficiency, and no skill contributes to any attribute rating.
 *
 * ON HISTORY
 *
 * `employee_skill` is append-only, so what the API returns is the *latest*
 * record per skill and the superseded ones are still stored. A row backed
 * by more than one record says so, because a current-state view that stays
 * silent about it reads as "this is all there has ever been". It says only
 * that — there is no endpoint returning those earlier records yet
 * (docs/API.md §12, Open Decision 10), and docs/UX_ARCHITECTURE.md §3.9
 * scopes MVP history to attribute assessments. Promising a history view by
 * making the count look clickable would be worse than the plain sentence.
 *
 * No trend, direction or progression is drawn from the count either: three
 * records is a number of records, not evidence of improvement.
 *
 * ON EVIDENCE (docs/DOMAIN_DECISIONS.md, "Skill Evidence")
 *
 * Evidence supports one specific record, not the skill in the abstract —
 * the same rule assessment evidence follows. There is no recording action
 * for a skill to attach evidence alongside (`employee_skill` has no write
 * path), so "Attach evidence" is offered here as its own per-skill action,
 * gated on `viewerCanAssessAs` — the same signal that already gates
 * writing an attribute assessment for this employee, reused rather than
 * duplicated for a second, narrower authorization question.
 * ---------------------------------------------------------------------
 */
export function SkillsSection({
  data,
  employeeId,
  viewerCanAssessAs,
  onEvidenceAttached,
}: {
  data: EmployeeSkillsResponse;
  employeeId: string;
  viewerCanAssessAs: SourceTypeCode | null;
  onEvidenceAttached: () => void;
}) {
  const [attachingTo, setAttachingTo] = useState<string | null>(null);

  return (
    <ul className={styles.list}>
      {data.skills.map((entry) => {
        const proficiency = PROFICIENCY_COPY[entry.proficiencyLevel];
        return (
          <li key={entry.id} className={styles.record}>
            <div className={styles.primary}>
              <span className={styles.name}>{entry.skill.name}</span>
              {entry.skill.category ? <span className={styles.category}>{entry.skill.category}</span> : null}
              <span className={styles.level} title={proficiency.meaning}>
                {proficiency.label}
              </span>
              {entry.skill.status === 'deprecated' ? (
                <span
                  className={styles.marker}
                  title="This skill has been retired from the organisation's skill library. The record is still held."
                >
                  Retired from the skill library
                </span>
              ) : null}
              {entry.recordCount > 1 ? (
                <span
                  className={styles.marker}
                  title="Earlier proficiency records for this skill are retained in storage. There is no view for them yet."
                >
                  {entry.recordCount} records held · showing the most recent
                </span>
              ) : null}
            </div>

            <dl className={styles.facts}>
              <div>
                <dt>Years recorded</dt>
                <dd>
                  {entry.yearsOfExperience === null ? (
                    <span className={styles.absent}>Not recorded</span>
                  ) : (
                    formatYears(entry.yearsOfExperience)
                  )}
                </dd>
              </div>
              <div>
                <dt>Recorded on</dt>
                <dd>{formatTimestamp(entry.assessedAt)}</dd>
              </div>
            </dl>

            {entry.evidence.length > 0 ? (
              <ul className={formStyles.evidenceSummary}>
                {entry.evidence.map((item) => (
                  <li key={item.id}>
                    <strong>{evidenceTypeLabel(item.evidenceTypeCode)}</strong>
                    {item.visibility === 'restricted' ? (
                      <span className={formStyles.restrictedBadge}>Restricted</span>
                    ) : null}
                    : {item.description}
                    {item.occurredOn ? ` (${formatCalendarDate(item.occurredOn)})` : ''}
                  </li>
                ))}
              </ul>
            ) : entry.evidenceCount > 0 ? (
              <p className={styles.absent}>
                {entry.evidenceCount} {entry.evidenceCount === 1 ? 'item' : 'items'} of evidence attached, not visible
                to you.
              </p>
            ) : null}

            {viewerCanAssessAs !== null ? (
              attachingTo === entry.id ? (
                <SkillEvidenceForm
                  employeeId={employeeId}
                  skillId={entry.id}
                  skillName={entry.skill.name}
                  onAttached={onEvidenceAttached}
                  onClose={() => setAttachingTo(null)}
                />
              ) : (
                <button type="button" className={captureStyles.addToggle} onClick={() => setAttachingTo(entry.id)}>
                  Attach evidence
                </button>
              )
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
